// src/components/canine-teeth-ai/SubImageAnnotator.js

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { debounce } from 'lodash'; // Import debounce da lodash
import { saveSession, getSessionByImageNameAndEmail } from '../../indexedDB';
import { flushSync } from 'react-dom';
import axios from 'axios';

import YoutubeSearchedForIcon from '@mui/icons-material/YoutubeSearchedFor';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import UndoIcon from '@mui/icons-material/Undo';
import SaveIcon from '@mui/icons-material/Save';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import DeleteTwoToneIcon from '@mui/icons-material/DeleteTwoTone';
import PolylineIcon from '@mui/icons-material/Polyline';
import EditIcon from '@mui/icons-material/Edit';
import SettingsIcon from '@mui/icons-material/Settings';
import FormatShapesTwoToneIcon from '@mui/icons-material/FormatShapesTwoTone';
import ContrastIcon from '@mui/icons-material/Contrast';
import LightModeIcon from '@mui/icons-material/LightMode';
import LineWeightIcon from '@mui/icons-material/LineWeight';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';

import { saveAs } from 'file-saver';
import html2canvas from 'html2canvas';
import '../tools.css';

function SubImageAnnotator({
  subImage,
  collectAnnotations,
  email,
  tipo,
  posizione,
  gender, // Passa il sesso se disponibile
  side, // Passa il lato se disponibile
  realAge: propRealAge,
  defaultMode = 'polygon',
  allowModeChange = false,
  selectableColors = ['blue', 'red'],
  initialAnnotations = null,
  updateSessionsCount,
}) {
  const tool = 'canine-teeth-ai';
  // Aggiungi questo riferimento per tracciare se il panning è avvenuto
  const hasPanned = useRef(false);

  // Aggiungi questa funzione per calcolare il centroide dei punti
  function computeCentroid(points) {
    const sum = points.reduce(
      (acc, point) => {
        acc.x += point.x;
        acc.y += point.y;
        return acc;
      },
      { x: 0, y: 0 }
    );
    return { x: sum.x / points.length, y: sum.y / points.length };
  }

  // Nuova: centralizza la logica delle formule per ottenere età, formula e margine d'errore
  const computeAgeFromRatio = useCallback(
    (ratio) => {
      const g = gender === 'M' ? 1 : 0;
      let ageEstimation = null;
      let formulaText = '';
      let age_err = null;

      if (!isFinite(ratio) || ratio <= 0) {
        return { ageEstimation: null, formulaText: '', age_err: null };
      }

      // Canine
      if (tipo === 'Canine') {
        if (posizione === 'Upper') {
          ageEstimation = 101.3 - 556.68 * ratio;
          formulaText = `101.3 - 556.68 * (${ratio.toFixed(2)})`;
          age_err = 2.68;
        } else if (posizione === 'Lower') {
          ageEstimation = 92.37 - 492.05 * ratio;
          formulaText = `92.37 - 492.05 * (${ratio.toFixed(2)})`;
          age_err = 2.68;
        }
        return { ageEstimation, formulaText, age_err };
      }

      // Central incisor
      if (tipo === 'Central incisor') {
        if (posizione === 'Upper') {
          ageEstimation = 78.55 - 3.86 * g - 313.45 * parseFloat(ratio.toFixed(2));
          formulaText = `78.55 - 3.86 * ${g} - 313.45 * (${ratio.toFixed(2)})`;
          age_err = 7.03;
        } else if (posizione === 'Lower') {
          ageEstimation = 76.48 - 4.85 * g - 332.09 * parseFloat(ratio.toFixed(2));
          formulaText = `76.48 - 4.85 * ${g} - 332.09 * (${ratio.toFixed(2)})`;
          age_err = 10.8;
        }
        return { ageEstimation, formulaText, age_err };
      }

      // Lateral incisor
      if (tipo === 'Lateral incisor') {
        if (posizione === 'Upper') {
          ageEstimation = 79.49 - 2.99 * g - 338.03 * parseFloat(ratio.toFixed(2));
          formulaText = `79.49 - 2.99 * ${g} - 338.03 * (${ratio.toFixed(2)})`;
          age_err = 6.64;
        } else if (posizione === 'Lower') {
          ageEstimation = 78.24 - 6.11 * g - 331.70 * parseFloat(ratio.toFixed(2));
          formulaText = `78.24 - 6.11 * ${g} - 331.70 * (${ratio.toFixed(2)})`;
          age_err = 10.9;
        }
        return { ageEstimation, formulaText, age_err };
      }

      // First Premolar Tooth
      if (tipo === 'First Premolar Tooth') {
        if (side === 'Left') {
          const R34 = parseFloat(ratio.toFixed(2));
          ageEstimation = 73.53 - 330.99 * Math.pow(R34, 1);
          formulaText = `73.53 - 330.99 * (${R34})`;
          age_err = 7.99;
        } else if (side === 'Right') {
          const R44 = parseFloat(ratio.toFixed(2));
          ageEstimation = 77.0 - 353.90 * Math.pow(R44, 0.73);
          formulaText = `77.0 - 353.90 * (${R44})`;
          age_err = 7.35;
        }
        return { ageEstimation, formulaText, age_err };
      }

      // Second Premolar Tooth
      if (tipo === 'Second Premolar Tooth') {
        if (side === 'Left') {
          const R35 = parseFloat(ratio.toFixed(2));
          ageEstimation = 70.27 - 299.96 * Math.pow(R35, 0.73);
          formulaText = `70.27 - 299.96 * (${R35})`;
          age_err = 7.59;
        } else if (side === 'Right') {
          const R45 = parseFloat(ratio.toFixed(2));
          ageEstimation = 76.29 - 362.57 * R45;
          formulaText = `76.29 - 362.57 * (${R45})`;
          age_err = 7.42;
        }
        return { ageEstimation, formulaText, age_err };
      }

      // Default fallback (nessuna formula nota)
      return { ageEstimation: null, formulaText: '', age_err: null };
    },
    [gender, tipo, posizione, side]
  );

  // Debounced saveCurrentSession function
  // eslint-disable-next-line
  const saveCurrentSessionDebounced = useCallback(
    debounce(async (sessionData) => {
      setIsSaving(true);
      await saveSession(sessionData);
      if (updateSessionsCount) {
        updateSessionsCount();
      }
      setIsSaving(false);
    }, 3000), // Debounce time of 3000ms (3 seconds)
    [] // Empty dependency array to create the function only once
  );

  // eslint-disable-next-line
  const [isSaving, setIsSaving] = useState(false);
  // Inizializza mouseDownPosition con valori null
  const [mouseDownPosition, setMouseDownPosition] = useState({ x: null, y: null });
  //const dragThreshold = 5;
  const panningThreshold = 5; // Pixel

  const handleResetZoom = useCallback(() => {
    setZoomLevel(1);
    setPanOffset({ x: 0, y: 0 });
  }, []);

  // Memorizza selectableColors se viene passato come prop inline
  // eslint-disable-next-line
  const memoizedSelectableColors = useMemo(() => selectableColors, []);

  const colorMap = useMemo(() => {
    const map = {};
    memoizedSelectableColors.forEach((color, index) => {
      map[color] = (index + 1).toString();
    });
    return map;
  }, [memoizedSelectableColors]);

  const lineKeys = useMemo(
    () => memoizedSelectableColors.map((color) => `line${colorMap[color]}`),
    [memoizedSelectableColors, colorMap]
  );

  const polygonKeys = useMemo(
    () => memoizedSelectableColors.map((color) => `polygon${colorMap[color]}`),
    [memoizedSelectableColors, colorMap]
  );

  const [coordinates, setCoordinates] = useState(() =>
    lineKeys.concat(polygonKeys).reduce((acc, key) => ({ ...acc, [key]: [] }), {})
  );

  const [ratios, setRatios] = useState({});
  const [areas, setAreas] = useState({});
  const [estimatedAge, setEstimatedAge] = useState(null);
  const [currentLine, setCurrentLine] = useState(lineKeys[0]);
  const [currentMode, setCurrentMode] = useState(defaultMode);
  const [lineThickness, setLineThickness] = useState(2);
  const [pointRadius, setPointRadius] = useState(5);
  const [internalRealAge, setInternalRealAge] = useState(propRealAge || '');
  const clickDisabled = useRef(false);
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const imageContainerRef = useRef(null);

  const [zoomLevel, setZoomLevel] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const minZoom = 0.5;
  const maxZoom = 5;
  // Definisci il margine di errore
  const [ageErrorMargin, setAgeErrorMagin] = useState(null);


  // Stato per il panning e il dragging
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [draggingPoint, setDraggingPoint] = useState(null);

  // Nuova variabile per il menu impostazioni/salvataggio unificato
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [showSaveMenu, setShowSaveMenu] = useState(false);


  // Stato per tracciare se i poligoni sono chiusi
  const [closedPolygons, setClosedPolygons] = useState(() =>
    polygonKeys.reduce((acc, key) => ({ ...acc, [key]: false }), {})
  );

  // Memorizza subImage se necessario
  // eslint-disable-next-line
  const memoizedSubImage = useMemo(() => subImage, [subImage.name, subImage.dataURL]);

  // Funzione helper per verificare se il mouse è vicino a un punto
  const isMouseOverAnyPoint = (x, y) => {
    const threshold = pointRadius / zoomLevel + 2; // Tolleranza per la vicinanza
    return Object.values(coordinates).some((points) =>
      points.some((point) => {
        const dx = x - point.x;
        const dy = y - point.y;
        return Math.sqrt(dx * dx + dy * dy) < threshold;
      })
    );
  };

  const lineKeyToColor = useMemo(() => {
    const map = {};
    memoizedSelectableColors.forEach((color) => {
      const lineNumber = colorMap[color];
      map[`line${lineNumber}`] = color;
      map[`polygon${lineNumber}`] = color;
    });
    return map;
  }, [memoizedSelectableColors, colorMap]);

  const getLineColor = useCallback((lineKey) => {
    return lineKeyToColor[lineKey] || '#f0f0f0';
  }, [lineKeyToColor]);

  const handleModeToggle = useCallback(() => {
    if (allowModeChange) {
      setCurrentMode((prevMode) => (prevMode === 'line' ? 'polygon' : 'line'));
    }
  }, [allowModeChange]);

  const handleLineChange = useCallback((newLine) => {
    setCurrentLine(newLine);
  }, []);

  const handleClosePolygon = useCallback(() => {
    if (currentMode === 'polygon') {
      const currentPolygonKey = `polygon${currentLine.charAt(currentLine.length - 1)}`;
      if (coordinates[currentPolygonKey].length >= 3 && !closedPolygons[currentPolygonKey]) {
        setClosedPolygons((prev) => ({ ...prev, [currentPolygonKey]: true }));
      }
    }
  }, [currentMode, currentLine, coordinates, closedPolygons]);

  const handleMouseMove = useCallback(
    (e) => {
      if (isDragging && draggingPoint) {
        // Handle dragging of points
        const containerRect = imageContainerRef.current.getBoundingClientRect();
        const x = e.clientX - containerRect.left;
        const y = e.clientY - containerRect.top;

        const img = imageRef.current;
        const scaleX = img.naturalWidth / img.clientWidth;
        const scaleY = img.naturalHeight / img.clientHeight;

        const imageX = ((x - panOffset.x) * scaleX) / zoomLevel;
        const imageY = ((y - panOffset.y) * scaleY) / zoomLevel;

        // Update the position of the dragged point
        setCoordinates((prevCoordinates) => {
          const updatedCoordinates = { ...prevCoordinates };
          updatedCoordinates[draggingPoint.key][draggingPoint.pointIndex] = {
            x: imageX,
            y: imageY,
          };
          return updatedCoordinates;
        });
        clickDisabled.current = true; // Disable click while dragging

        // Set cursor to 'move' during dragging
        if (canvasRef.current) {
          canvasRef.current.style.cursor = 'move';
        }
      } else if (isPanning) {
        e.preventDefault(); // Prevent default behavior during panning

        const newPanOffset = {
          x: e.clientX - panStart.x,
          y: e.clientY - panStart.y,
        };
        setPanOffset(newPanOffset);
        hasPanned.current = true; // Indicate that panning has occurred

        // Set cursor to 'grabbing' during panning
        if (canvasRef.current) {
          canvasRef.current.style.cursor = 'grabbing';
        }
      } else if (mouseDownPosition.x !== null && mouseDownPosition.y !== null) {
        const deltaX = e.clientX - mouseDownPosition.x;
        const deltaY = e.clientY - mouseDownPosition.y;
        const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

        if (distance > panningThreshold && zoomLevel > 1) {
          setIsPanning(true);
          setPanStart({
            x: mouseDownPosition.x - panOffset.x,
            y: mouseDownPosition.y - panOffset.y,
          });
          clickDisabled.current = true; // Prevent adding a point during panning
        } else {
          // Set cursor to 'crosshair' when not panning or dragging
          if (canvasRef.current) {
            canvasRef.current.style.cursor = 'crosshair';
          }
        }
      } else {
        // Verifica se il mouse è sopra un punto
        const containerRect = imageContainerRef.current.getBoundingClientRect();
        const x = e.clientX - containerRect.left;
        const y = e.clientY - containerRect.top;

        const img = imageRef.current;
        const scaleX = img.naturalWidth / img.clientWidth;
        const scaleY = img.naturalHeight / img.clientHeight;

        const imageX = ((x - panOffset.x) * scaleX) / zoomLevel;
        const imageY = ((y - panOffset.y) * scaleY) / zoomLevel;

        if (isMouseOverAnyPoint(imageX, imageY)) {
          canvasRef.current.style.cursor = 'move'; // Cambia il cursore in 'move' quando sopra un punto
        } else {
          canvasRef.current.style.cursor = 'crosshair'; // Altrimenti, 'crosshair'
        }
      }
    },
    // eslint-disable-next-line
    [
      isDragging,
      draggingPoint,
      isPanning,
      panStart.x,
      panStart.y,
      panOffset.x,
      panOffset.y,
      zoomLevel,
      mouseDownPosition,
      panningThreshold,
      coordinates, // Aggiungi coordinates alle dipendenze
    ]
  );

  const handleMouseUp = useCallback(() => {
    setIsPanning(false);
    setIsDragging(false);
    setDraggingPoint(null);
    setMouseDownPosition({ x: null, y: null }); // Reset della posizione del mouse
    // Ripristina il cursore su 'crosshair'
    if (canvasRef.current) {
      canvasRef.current.style.cursor = 'crosshair';
    }
  }, []);

  const handleCanvasClick = useCallback(
    (e) => {
      if (isDragging || isPanning || hasPanned.current) {
        hasPanned.current = false; // Reset dopo aver ignorato il click
        return; // Ignora il click
      }
      if (clickDisabled.current) {
        clickDisabled.current = false; // Reset del flag
        return; // Non aggiungere un nuovo punto
      }

      const containerRect = imageContainerRef.current.getBoundingClientRect();
      const x = e.clientX - containerRect.left;
      const y = e.clientY - containerRect.top;

      const img = imageRef.current;
      const scaleX = img.naturalWidth / img.clientWidth;
      const scaleY = img.naturalHeight / img.clientHeight;

      const imageX = ((x - panOffset.x) * scaleX) / zoomLevel;
      const imageY = ((y - panOffset.y) * scaleY) / zoomLevel;

      setCoordinates((prevCoordinates) => {
        const updatedCoordinates = { ...prevCoordinates };
        const key =
          currentMode === 'line'
            ? currentLine
            : `polygon${currentLine.charAt(currentLine.length - 1)}`;

        if (currentMode === 'line') {
          if (updatedCoordinates[key].length < 2) {
            updatedCoordinates[key] = [...updatedCoordinates[key], { x: imageX, y: imageY }];
          }
        } else if (currentMode === 'polygon') {
          if (!closedPolygons[key]) {
            updatedCoordinates[key] = [...updatedCoordinates[key], { x: imageX, y: imageY }];
          }
        }

        return updatedCoordinates;
      });
      // Reset di hasPanned.current dopo aver aggiunto un punto
      hasPanned.current = false;
    },
    [
      isDragging,
      isPanning,
      clickDisabled,
      panOffset.x,
      panOffset.y,
      zoomLevel,
      currentMode,
      currentLine,
      closedPolygons,
    ]
  );

  const handleMouseDown = useCallback(
    (e) => {
      hasPanned.current = false; // Reset del flag
      setMouseDownPosition({ x: e.clientX, y: e.clientY });

      const containerRect = imageContainerRef.current.getBoundingClientRect();
      const x = e.clientX - containerRect.left;
      const y = e.clientY - containerRect.top;

      const img = imageRef.current;
      const scaleX = img.naturalWidth / img.clientWidth;
      const scaleY = img.naturalHeight / img.clientHeight;

      const imageX = ((x - panOffset.x) * scaleX) / zoomLevel;
      const imageY = ((y - panOffset.y) * scaleY) / zoomLevel;

      let foundPoint = null;
      let foundKey = null;

      Object.entries(coordinates).forEach(([key, points]) => {
        points.forEach((point, idx) => {
          const dx = imageX - point.x;
          const dy = imageY - point.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance < pointRadius / zoomLevel + 2) {
            foundPoint = idx;
            foundKey = key;
          }
        });
      });

      if (foundPoint !== null && foundKey !== null) {
        // Inizia a trascinare il punto
        setIsDragging(true);
        setDraggingPoint({ key: foundKey, pointIndex: foundPoint });
        clickDisabled.current = false; // Reset del flag
      } else {
        clickDisabled.current = false; // Consenti l'aggiunta di un nuovo punto
      }
    },
    [panOffset.x, panOffset.y, zoomLevel, pointRadius, coordinates]
  );

  const handleWheel = useCallback(
    (e) => {
      e.preventDefault();

      const containerRect = imageContainerRef.current.getBoundingClientRect();
      const x = e.clientX - containerRect.left;
      const y = e.clientY - containerRect.top;

      const zoomAmount = e.deltaY * -0.001;
      const newZoomLevel = Math.min(Math.max(zoomLevel + zoomAmount, minZoom), maxZoom);
      const zoomRatio = newZoomLevel / zoomLevel;

      const newPanOffset = {
        x: x - (x - panOffset.x) * zoomRatio,
        y: y - (y - panOffset.y) * zoomRatio,
      };

      setZoomLevel(newZoomLevel);
      setPanOffset(newPanOffset);
    },
    [zoomLevel, panOffset.x, panOffset.y, minZoom, maxZoom]
  );

  const handleReset = useCallback(() => {
    if (currentMode === 'line') {
      setCoordinates((prevCoordinates) => ({
        ...prevCoordinates,
        [currentLine]: [],
      }));
      setRatios((prevRatios) => {
        const updatedRatios = { ...prevRatios };
        delete updatedRatios[currentLine];
        return updatedRatios;
      });
    } else if (currentMode === 'polygon') {
      const polygonKey = `polygon${currentLine.charAt(currentLine.length - 1)}`;
      setCoordinates((prevCoordinates) => ({
        ...prevCoordinates,
        [polygonKey]: [],
      }));
      setAreas((prevAreas) => {
        const updatedAreas = { ...prevAreas };
        delete updatedAreas[polygonKey];
        return updatedAreas;
      });
      setClosedPolygons((prevClosed) => ({
        ...prevClosed,
        [polygonKey]: false,
      }));
    }
  }, [currentMode, currentLine]);

  const handleUndoLastPoint = useCallback(() => {
    const key =
      currentMode === 'line'
        ? currentLine
        : `polygon${currentLine.charAt(currentLine.length - 1)}`;
    setCoordinates((prevCoordinates) => {
      const updatedCoordinates = { ...prevCoordinates };
      if (updatedCoordinates[key].length > 0) {
        updatedCoordinates[key] = updatedCoordinates[key].slice(0, -1);
      }
      return updatedCoordinates;
    });
    if (currentMode === 'polygon') {
      const polygonKey = key;
      setClosedPolygons((prevClosed) => ({
        ...prevClosed,
        [polygonKey]: false,
      }));
    }
  }, [currentMode, currentLine]);

  // Funzioni calcolate
  const calculatePolygonArea = useCallback((points) => {
    let area = 0;
    const n = points.length;
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      area += points[i].x * points[j].y;
      area -= points[j].x * points[i].y;
    }
    return Math.abs(area) / 2;
  }, []);

  const calculateLineLength = useCallback((points) => {
    if (points.length === 2) {
      const dx = points[1].x - points[0].x;
      const dy = points[1].y - points[0].y;
      return Math.sqrt(dx * dx + dy * dy);
    }
    return 0;
  }, []);

  const saveCurrentSession = useCallback(() => {
    const sessionData = {
      subImage: {
        ...memoizedSubImage,
        name: memoizedSubImage.name,
      },
      tool,
      coordinates,
      ratios,
      areas,
      closedPolygons,
      estimatedAge,
      email,
      tipo,
      posizione,
      gender,
      side,
      realAge: internalRealAge,
      timestamp: new Date().toISOString(),
    };
    saveCurrentSessionDebounced(sessionData);
  }, [
    coordinates,
    ratios,
    areas,
    closedPolygons,
    estimatedAge,
    memoizedSubImage,
    email,
    tipo,
    posizione,
    gender,
    side,
    internalRealAge,
    saveCurrentSessionDebounced,
  ]);

  // Aggiorna l'uso di useEffect per il salvataggio
  useEffect(() => {
    saveCurrentSession();
  }, [coordinates, ratios, areas, closedPolygons, estimatedAge, saveCurrentSession]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      saveCurrentSessionDebounced.cancel();
    };
  }, [saveCurrentSessionDebounced]);

  // useEffect per caricare la sessione salvata
  useEffect(() => {
    const loadSavedSession = async () => {
      const savedSession = await getSessionByImageNameAndEmail(
        memoizedSubImage.name,
        email
      );
      if (savedSession) {
        setCoordinates(savedSession.coordinates || {});
        setRatios(savedSession.ratios || {});
        setAreas(savedSession.areas || {});
        setClosedPolygons(savedSession.closedPolygons || {});
        setEstimatedAge(savedSession.estimatedAge || null);
        setInternalRealAge(savedSession.realAge || '');
      } else if (initialAnnotations) {
        setCoordinates(initialAnnotations.coordinates || {});
        setRatios(initialAnnotations.ratios || {});
        setAreas(initialAnnotations.areas || {});
        setClosedPolygons(initialAnnotations.closedPolygons || {});
        setEstimatedAge(initialAnnotations.estimatedAge || null);
        setInternalRealAge(initialAnnotations.realAge || '');
      }
    };
    loadSavedSession();
  }, [memoizedSubImage.name, email, initialAnnotations]);

  // Aggiorna internalRealAge quando cambia il prop realAge
  useEffect(() => {
    setInternalRealAge(propRealAge || '');
  }, [propRealAge]);

  // Modifica anche il calcolo dell'area nel useEffect corrispondente:
  useEffect(() => {
    const updatedAreas = {};
    polygonKeys.forEach((polygonKey) => {
      const points = coordinates[polygonKey];
      if (points.length >= 3 && closedPolygons[polygonKey]) {
        const centroid = computeCentroid(points);
        const pointsWithAngles = points.map((point) => {
          const angle = Math.atan2(point.y - centroid.y, point.x - centroid.x);
          return { ...point, angle };
        });
        const sortedPoints = pointsWithAngles.sort((a, b) => a.angle - b.angle);

        const area = calculatePolygonArea(sortedPoints);
        updatedAreas[polygonKey] = area;
      } else if (areas[polygonKey]) {
        updatedAreas[polygonKey] = undefined;
      }
    });
    setAreas((prevAreas) => ({ ...prevAreas, ...updatedAreas }));

    const updatedRatios = {};
    lineKeys.forEach((lineKey) => {
      const points = coordinates[lineKey];
      if (points.length === 2) {
        const length = calculateLineLength(points);
        updatedRatios[lineKey] = length;
      } else if (ratios[lineKey]) {
        updatedRatios[lineKey] = undefined;
      }
    });
    setRatios((prevRatios) => ({ ...prevRatios, ...updatedRatios }));
    // eslint-disable-next-line
  }, [coordinates, polygonKeys, lineKeys, calculatePolygonArea, calculateLineLength, closedPolygons]);
  // eslint-disable-next-line
  const [ageFormula, setAgeFormula] = useState('');


  useEffect(() => {
    if (
      typeof areas['polygon1'] === 'number' &&
      typeof areas['polygon2'] === 'number' &&
      areas['polygon1'] > 0 &&
      closedPolygons['polygon1'] &&
      closedPolygons['polygon2']
    ) {
  const ratio = areas['polygon2'] / areas['polygon1'];
  const { ageEstimation, formulaText, age_err } = computeAgeFromRatio(ratio);
  setEstimatedAge(ageEstimation);
  setAgeFormula(formulaText);
  setAgeErrorMagin(age_err);
    } else {
      setEstimatedAge(null);
      setAgeFormula('');
    }
  }, [areas, posizione, side, tipo, gender, closedPolygons, computeAgeFromRatio]);

  // Nel tuo componente SubImageAnnotator, modifica il useEffect che disegna il canvas:
  useEffect(() => {
    const canvas = canvasRef.current;
    const img = imageRef.current;

    if (canvas && img) {
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const drawCanvas = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        Object.entries(coordinates).forEach(([key, points]) => {
          const isLine = key.startsWith('line');
          const color = getLineColor(key);

          ctx.fillStyle = color;
          ctx.strokeStyle = color;

          if (isLine) {
            // Codice per disegnare linee (rimane invariato)
            if (points.length > 0) {
              ctx.beginPath();
              ctx.moveTo(points[0].x, points[0].y);
              for (let i = 1; i < points.length; i++) {
                ctx.lineTo(points[i].x, points[i].y);
              }
              ctx.lineWidth = lineThickness;
              ctx.stroke();

              // Disegna i punti
              points.forEach((point) => {
                ctx.beginPath();
                ctx.arc(point.x, point.y, pointRadius, 0, 2 * Math.PI);
                ctx.fill();
              });
            }
          } else {
            if (points.length > 0) {
              ctx.beginPath();
              ctx.moveTo(points[0].x, points[0].y);
              for (let i = 1; i < points.length; i++) {
                ctx.lineTo(points[i].x, points[i].y);
              }
              if (closedPolygons[key] && points.length > 2) {
                ctx.closePath();
                if (areas[key]) {
                  ctx.globalAlpha = 0.3;
                  ctx.fillStyle = color;
                  ctx.fill();
                  ctx.globalAlpha = 1.0;
                }
              }
              ctx.lineWidth = lineThickness;
              ctx.stroke();

              // Disegna i punti
              points.forEach((point) => {
                ctx.beginPath();
                ctx.arc(point.x, point.y, pointRadius, 0, 2 * Math.PI);
                ctx.fill();
              });
            }
          }
        });
      };

      let animationFrameId = requestAnimationFrame(drawCanvas);

      return () => {
        cancelAnimationFrame(animationFrameId);
      };
    }
  }, [coordinates, lineThickness, pointRadius, areas, getLineColor, closedPolygons]);

  useEffect(() => {
    const container = imageContainerRef.current;
    if (!container) return;

    container.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, [handleWheel]);


  // Funzione per resettare la vista e salvare l'immagine
  const handleDownloadAnnotatedImage = useCallback(() => {
    // Salva lo stato corrente di zoom e pan
    // const previousZoom = zoomLevel;
    // const previousPan = { ...panOffset };

    // Resetta zoom e pan sincronamente per assicurarti che il DOM si aggiorni prima della cattura
    flushSync(() => {
      setZoomLevel(1);
      setPanOffset({ x: 0, y: 0 });
    });

    // Effettua la cattura dopo che lo stato è stato aggiornato
    html2canvas(imageContainerRef.current, { useCORS: true }).then((canvas) => {
      canvas.toBlob((blob) => {
        saveAs(blob, memoizedSubImage.name);
        // // Ripristina lo stato originale di zoom e pan
        // setZoomLevel(previousZoom);
        // setPanOffset(previousPan);
      }, 'image/png', 1);
    });
  }, [memoizedSubImage.name]);


  // Nuova funzione per salvare le annotazioni in formato COCO
  const handleSaveAnnotationsCOCO = useCallback(() => {
    if (!imageRef.current) return;
    const imgWidth = imageRef.current.naturalWidth;
    const imgHeight = imageRef.current.naturalHeight;
    const imageID = 1; // Puoi adattare l'ID se gestisci più immagini

    let annotationsCOCO = [];
    let annotationId = 1;

    // Elaboriamo solo le annotazioni relative ai poligoni
    Object.keys(coordinates).forEach((key) => {
      if (key.startsWith('polygon')) {
        const points = coordinates[key];
        if (points.length >= 3 && closedPolygons[key]) {
          // Determina la categoria in base al colore:
          // Se il colore è "red", la categoria è "pulp" (id:2); se è "blue", la categoria è "teeth" (id:1)
          const color = getLineColor(key);
          let categoryName = null;
          let categoryId = null;
          if (color === 'red') {
            categoryName = 'pulp';
            categoryId = 2;
          } else if (color === 'blue') {
            categoryName = 'teeth';
            categoryId = 1;
          }
          if (!categoryName) return; // ignora se non riconosciuto

          // Crea la segmentation in formato COCO (array flat di coordinate)
          const segmentation = points.reduce((acc, pt) => {
            acc.push(pt.x, pt.y);
            return acc;
          }, []);

          // Calcola il bbox come [min_x, min_y, width, height]
          const xs = points.map((pt) => pt.x);
          const ys = points.map((pt) => pt.y);
          const minX = Math.min(...xs);
          const minY = Math.min(...ys);
          const maxX = Math.max(...xs);
          const maxY = Math.max(...ys);
          const bbox = [minX, minY, maxX - minX, maxY - minY];
          const area = calculatePolygonArea(points);

          annotationsCOCO.push({
            id: annotationId,
            image_id: imageID,
            category_id: categoryId,
            segmentation: [segmentation],
            area: area,
            bbox: bbox,
            iscrowd: 0,
          });
          annotationId++;
        }
      }
    });

    const cocoData = {
      images: [
        {
          id: imageID,
          file_name: memoizedSubImage.name,
          width: imgWidth,
          height: imgHeight,
        },
      ],
      annotations: annotationsCOCO,
      categories: [
        { id: 1, name: 'teeth', supercategory: 'dental' },
        { id: 2, name: 'pulp', supercategory: 'dental' },
      ],
    };

    const blob = new Blob([JSON.stringify(cocoData, null, 2)], { type: 'application/json' });
    saveAs(blob, `${memoizedSubImage.name.replace(/\.[^/.]+$/, '')}_annotations_coco.json`);
  }, [coordinates, closedPolygons, memoizedSubImage, calculatePolygonArea, getLineColor]);


  const handleSaveAnnotationsJSON = useCallback(() => {
    const data = {
      imageName: memoizedSubImage.name,
      annotations: coordinates,
      ratios: ratios,
      areas: areas,
      closedPolygons: closedPolygons,
      estimatedAge: estimatedAge,
      email: email,
      tipo: tipo,
      posizione: posizione,
      gender: gender, // Passa il sesso se disponibile
      side: side, // Passa il lato se disponibile
      centerPoint: memoizedSubImage.center,
      timestamp: new Date().toISOString(),
      subImageDimensions: {
        width: imageRef.current.naturalWidth,
        height: imageRef.current.naturalHeight,
      },
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    saveAs(blob, `${memoizedSubImage.name.replace(/\.[^/.]+$/, '')}_annotations.json`);
  }, [
    memoizedSubImage,
    coordinates,
    ratios,
    areas,
    closedPolygons,
    estimatedAge,
    email,
    tipo,
    posizione,
    gender,
    side,
  ]);

  const [contrast, setContrast] = useState(100); // Valore di default 100% (normale)
  const [brightness, setBrightness] = useState(100); // Valore di default 100% (normale)

  const handleContrastChange = useCallback((e) => {
    setContrast(e.target.value);
  }, []);

  const handleBrightnessChange = useCallback((e) => {
    setBrightness(e.target.value);
  }, []);

  const handleMagicAI = async () => {
    try {
      // Clear existing annotations
      const newCoordinates = {};
      lineKeys.concat(polygonKeys).forEach((key) => {
        newCoordinates[key] = [];
      });
      setCoordinates(newCoordinates);
      setClosedPolygons({});

      // Create a temporary canvas to apply current filters
      const tempCanvas = document.createElement('canvas');
      const tempCtx = tempCanvas.getContext('2d');
      
      // Load the image
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.src = memoizedSubImage.dataURL;

      image.onload = async () => {
        // Set canvas dimensions
        tempCanvas.width = image.width;
        tempCanvas.height = image.height;
        
        // Draw the image
        tempCtx.drawImage(image, 0, 0);
        
        // Apply current filters
        tempCtx.filter = `contrast(${contrast}%) brightness(${brightness}%)`;
        tempCtx.drawImage(tempCanvas, 0, 0);
        
        // Get the modified image data
        const base64Image = tempCanvas.toDataURL('image/jpeg').split(',')[1];

        const roboflowUrl = 'https://serverless.roboflow.com/finaltoothpulp05032025instance-wtall/2?api_key=CaA6CQPKRxRnK4QGzEvR';

        const { data } = await axios.post(roboflowUrl, base64Image, {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          }
        });

        const toothPrediction = data.predictions.find(p => p.class === 'tooth' && p.confidence >= 0.02);
        const pulpPrediction = data.predictions.find(p => p.class === 'pulp' && p.confidence >= 0.02);

        if (!toothPrediction && !pulpPrediction) {
          alert('Unable to perform automatic annotation: no relevant objects detected');
          return;
        }

        // Aggregate close points
        const aggregateClosePoints = (points, threshold) => {
          let clusters = [];

          points.forEach(pt => {
            let added = false;

            for (let cluster of clusters) {
              const dist = Math.sqrt(
                Math.pow(cluster.x - pt.x, 2) +
                Math.pow(cluster.y - pt.y, 2)
              );

              if (dist <= threshold) {
                cluster.x = (cluster.x * cluster.count + pt.x) / (cluster.count + 1);
                cluster.y = (cluster.y * cluster.count + pt.y) / (cluster.count + 1);
                cluster.count += 1;
                added = true;
                break;
              }
            }

            if (!added) {
              clusters.push({ ...pt, count: 1 });
            }
          });

          return clusters.map(({ x, y }) => ({ x, y }));
        };

        const threshold = 10;

        const adaptPoints = (points) => points.map(pt => ({
          x: pt.x,
          y: pt.y,
        }));

        const polygon1Raw = toothPrediction ? adaptPoints(toothPrediction.points) : [];
        const polygon2Raw = pulpPrediction ? adaptPoints(pulpPrediction.points) : [];

        // Apply aggregation to polygon points
        const polygon1 = aggregateClosePoints(polygon1Raw, threshold);
        const polygon2 = aggregateClosePoints(polygon2Raw, threshold);

        const updatedCoordinates = {
          ...newCoordinates,
          ...(polygon1.length > 0 && { polygon1 }), // tooth (blu)
          ...(polygon2.length > 0 && { polygon2 }), // pulp (rosso)
        };

        setCoordinates(updatedCoordinates);
        setClosedPolygons({
          ...(polygon1.length > 0 && { polygon1: true }),
          ...(polygon2.length > 0 && { polygon2: true }),
        });

        if (toothPrediction && pulpPrediction) {
          alert('Successful AI Age Estimation!');
        } else if (toothPrediction) {
          alert('Tooth detected, pulp not found.');
        } else {
          alert('Pulp detected, tooth not found.');
        }
      };

      image.onerror = () => {
        alert('Error loading image.');
      };

    } catch (error) {
      console.error(error);
      alert('Error with automatic annotation.');
    }
  };
  


  // Calcola l'età minima e massima
  const lowerAge =
    estimatedAge !== null && !isNaN(estimatedAge)
      ? (estimatedAge - ageErrorMargin).toFixed(2)
      : null;

  const upperAge =
    estimatedAge !== null && !isNaN(estimatedAge)
      ? (estimatedAge + ageErrorMargin).toFixed(2)
      : null;

  // Calcola la differenza tra età stimata e reale
  const ageDifference = internalRealAge && estimatedAge ? (estimatedAge - parseFloat(internalRealAge)).toFixed(2) : null;

  const [activeSetting, setActiveSetting] = useState(null);

  const handleSettingClick = (setting) => {
    setActiveSetting(activeSetting === setting ? null : setting);
  };

  // Aggiungi queste costanti all'inizio del componente, dopo le altre costanti
  const DEFAULT_CONTRAST = 100;
  const DEFAULT_BRIGHTNESS = 100;
  const DEFAULT_LINE_THICKNESS = 2;
  const DEFAULT_POINT_RADIUS = 5;

  // Aggiungi queste funzioni di reset
  const handleResetContrast = useCallback(() => {
    setContrast(DEFAULT_CONTRAST);
  }, []);

  const handleResetBrightness = useCallback(() => {
    setBrightness(DEFAULT_BRIGHTNESS);
  }, []);

  const handleResetLineThickness = useCallback(() => {
    setLineThickness(DEFAULT_LINE_THICKNESS);
  }, []);

  const handleResetPointRadius = useCallback(() => {
    setPointRadius(DEFAULT_POINT_RADIUS);
  }, []);

  return (
    <div className="subimage-container">
      <h2>{posizione} {tipo} - {memoizedSubImage.name}</h2>

      <div className="tooltips">
        <div className="tooltip">
          <p>Use 🔵 color to select edge points of the tooth</p>
        </div>
        <div className="tooltip">
          <p>Use 🔴 color to select edges of the dental pulp</p>
        </div>
      </div>

      <div className="subimage-topbar">
        {allowModeChange && (
          <button
            onClick={handleModeToggle}
            className="topbar-button"
            title={currentMode === 'line' ? 'Switch to Polygon Mode' : 'Switch to Line Mode'}
          >
            {currentMode === 'line' ? (
              <PolylineIcon className="topbar-icon" />
            ) : (
              <EditIcon className="topbar-icon" />
            )}
          </button>
        )}

        <div className="icon-group">
          {lineKeys.map((line) => (
            <button
              key={line}
              onClick={() => handleLineChange(line)}
              className={`topbar-button ${currentLine === line ? 'active-line' : ''}`}
              title={`Color ${lineKeyToColor[line]}`}
              style={{ backgroundColor: getLineColor(line) }}
            >
              <RemoveCircleOutlineIcon className="topbar-icon" />
            </button>
          ))}
          {currentMode === 'polygon' && (
            <button onClick={handleClosePolygon} className="topbar-button" title="Close Polygon">
              <FormatShapesTwoToneIcon className="topbar-icon" />
            </button>
          )}
        </div>

        <div className="icon-group">
          <button
            onClick={handleMagicAI}
            className="topbar-button"
            title="Auto Annotate (AI)"
          >
            <AutoFixHighIcon className="topbar-icon" />
          </button>
        </div>

        <div className="icon-group">
          <button onClick={handleUndoLastPoint} className="topbar-button" title="Remove Last Point">
            <UndoIcon className="topbar-icon" />
          </button>
          <button onClick={handleReset} className="topbar-button" title="Remove all points">

            <DeleteTwoToneIcon className="topbar-icon" />
          </button>
        </div>

        <div className="icon-group">
          <button onClick={handleResetZoom} className="topbar-button" title="Reset Zoom">
            <YoutubeSearchedForIcon className="topbar-icon" />
          </button>
        </div>



        <div className="icon-group">
          <button
            onClick={() => setShowSaveMenu(!showSaveMenu)}
            className="topbar-button"
            title="Save"
          >
            <SaveIcon className="topbar-icon" />
          </button>
          <button
            onClick={() => setShowSettingsMenu(!showSettingsMenu)}
            className={`topbar-button ${showSettingsMenu ? 'active-settings' : ''}`}
            title="Settings"
          >
            <SettingsIcon className="topbar-icon" />
          </button>
        </div>

      </div>

      {showSettingsMenu && (
        <div className="subimage-topbar settings-menu">
          <div className="settings-icons-container">
            <button
              className={`settings-icon-button ${activeSetting === 'contrast' ? 'active' : ''}`}
              onClick={() => handleSettingClick('contrast')}
              title="Contrast"
            >
              <ContrastIcon className="topbar-icon" />
            </button>
            <button
              className={`settings-icon-button ${activeSetting === 'brightness' ? 'active' : ''}`}
              onClick={() => handleSettingClick('brightness')}
              title="Brightness"
            >
              <LightModeIcon className="topbar-icon" />
            </button>
            <button
              className={`settings-icon-button ${activeSetting === 'lineThickness' ? 'active' : ''}`}
              onClick={() => handleSettingClick('lineThickness')}
              title="Line Thickness"
            >
              <LineWeightIcon className="topbar-icon" />
            </button>
            <button
              className={`settings-icon-button ${activeSetting === 'pointRadius' ? 'active' : ''}`}
              onClick={() => handleSettingClick('pointRadius')}
              title="Point Size"
            >
              <RadioButtonCheckedIcon className="topbar-icon" />
            </button>
          </div>

          <div className={`slider-container ${activeSetting === 'contrast' ? 'visible' : ''}`}>
            <input
              type="range"
              min="50"
              max="200"
              value={contrast}
              onChange={handleContrastChange}
            />
            <div className="slider-value">
              {contrast}%
              <span className="reset-icon" onClick={handleResetContrast}>🔄</span>
            </div>
          </div>

          <div className={`slider-container ${activeSetting === 'brightness' ? 'visible' : ''}`}>
            <input
              type="range"
              min="50"
              max="200"
              value={brightness}
              onChange={handleBrightnessChange}
            />
            <div className="slider-value">
              {brightness}%
              <span className="reset-icon" onClick={handleResetBrightness}>🔄</span>
            </div>
          </div>

          <div className={`slider-container ${activeSetting === 'lineThickness' ? 'visible' : ''}`}>
            <input
              type="range"
              min="1"
              max="5"
              value={lineThickness}
              onChange={(e) => setLineThickness(parseInt(e.target.value))}
            />
            <div className="slider-value">
              {lineThickness}
              <span className="reset-icon" onClick={handleResetLineThickness}>🔄</span>
            </div>
          </div>

          <div className={`slider-container ${activeSetting === 'pointRadius' ? 'visible' : ''}`}>
            <input
              type="range"
              min="3"
              max="10"
              value={pointRadius}
              onChange={(e) => setPointRadius(parseInt(e.target.value))}
            />
            <div className="slider-value">
              {pointRadius}
              <span className="reset-icon" onClick={handleResetPointRadius}>🔄</span>
            </div>
          </div>
        </div>
      )}

      {showSaveMenu && (
        <div className="subimage-topbar save-menu">
          <div className="save-buttons-container">
            <button
              onClick={handleDownloadAnnotatedImage}
              className="save-button"
              title="Save Image"
            >
              <FileDownloadIcon className="button-icon" />
              Save Image
            </button>
            <button
              onClick={handleSaveAnnotationsJSON}
              className="save-button"
              title="Save Annotations (JSON)"
            >
              <SaveIcon className="button-icon" />
              Save Annotations
            </button>
            {/* Nuovo pulsante per salvare le annotazioni in formato COCO */}
            <button
              onClick={handleSaveAnnotationsCOCO}
              className="save-button"
              title="Save Annotations (COCO)"
            >
              <SaveIcon className="button-icon" />
              Save Annotations COCO
            </button>
          </div>
        </div>
      )}



      <div className="annotator-container">
        <div
          className="image-container"
          ref={imageContainerRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <div
            className="image-wrapper"
            style={{
              transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoomLevel})`,
              transformOrigin: '0 0',
            }}
          >
            <img
              src={memoizedSubImage.dataURL}
              alt="Selected sub-image"
              ref={imageRef}
              className="annotator-image"
              crossOrigin="anonymous"
              style={{ filter: `contrast(${contrast}%) brightness(${brightness}%)` }}
              onLoad={() => {
                const img = imageRef.current;
                const canvas = canvasRef.current;
                if (img && canvas) {
                  canvas.width = img.naturalWidth;
                  canvas.height = img.naturalHeight;
                }
              }}
            />
            <canvas
              ref={canvasRef}
              className="annotator-canvas"
              onClick={handleCanvasClick}
              style={{ width: '100%', height: '100%' }}
            />
          </div>
        </div>
      </div>
      
      {estimatedAge !== null && !isNaN(estimatedAge) && (
        <div className="results-footer">
          {/* Sezione Risultati */}
          <div className="results-grid single-column">
            {/* Card principale dei risultati */}
            <div className="result-card">
              <div className="result-header">
                <h4>Canine Teeth AI Age Estimation</h4>
              </div>
              <div className="result-values">
                <div className="value-item">
                  <span className="value-number">{lowerAge}</span>
                  <span className="value-label">Min Age (yrs)</span>
                </div>
                <div className="value-divider">|</div>
                <div className="value-item">
                  <span className="value-number">{estimatedAge.toFixed(2)}</span>
                  <span className="value-label">Estimated Age (yrs)</span>
                </div>
                <div className="value-divider">|</div>
                <div className="value-item">
                  <span className="value-number">{upperAge}</span>
                  <span className="value-label">Max Age (yrs)</span>
                </div>
              </div>
              
              {ageDifference !== null && (
                <div className="estimated-result">
                  <div className="age-comparison-modern">
                    <div className="real-age-display">
                      <span className="real-age-value">{internalRealAge} yrs</span>
                      <span className="real-age-label">Real Age</span>
                    </div>
                    <div className="difference-display">
                      <span className={`difference-value-modern ${Math.abs(ageDifference) > ageErrorMargin ? 'outside-margin' : 'within-margin'}`}>
                        {ageDifference > 0 ? '+' : ''}{ageDifference} yrs
                      </span>
                      <span className="difference-label-modern">
                        {Math.abs(ageDifference) > ageErrorMargin ? 'Outside Error Margin' : 'Within Error Margin'}
                      </span>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="meta-info">
                <span className="region-info">AI-Enhanced Dental Analysis</span>
                {internalRealAge && <span className="real-age-info">Real: {internalRealAge} yrs</span>}
              </div>
            </div>
          </div>

          {/* Actions Section */}
          <div className="actions-section" style={{ marginBottom: 12 }}>
            <a
              href="https://cebi.shinyapps.io/AdultsAgeEstimationApp/"
              target="_blank"
              rel="noopener noreferrer"
              className="action-button primary"
              style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}
            >
              <span style={{ marginRight: 8 }}>Age Estimation App for Adults</span>
            </a>
          </div>
        </div>
      )}
      
      <div className="bibliography-section">
        <h4>Bibliography</h4>
        <p className="bibliography-text">
          <strong>Age estimation by pulp/tooth ratio in lateral and central incisors by peri-apical X-ray.</strong><br/>
          Cameriere R, Cunha E, Wasterlain SN, De Luca S, Sassaroli E, Pagliara F, Nuzzolese E, Cingolani M, Ferrante L.<br/>
          <em>J Forensic Leg Med. 2013 Jul;20(5):530-6. doi: 10.1016/j.jflm.2013.02.012. Epub 2013 Apr 6. PMID: 23756528.</em>
        </p>
      </div>
    </div>
  );

}

export default SubImageAnnotator;
